import { useEffect, useState } from 'react';

import { LogoutIcon, TrashIcon } from '@braskai/icons';
import { Button, SelectItem, SpeakersPaletteTokens } from '@braskai/ui-kit';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { z } from 'zod';

import { SomethingWrongError } from '@/widgets/error';

import { useTeamspaceSettingsModal } from '@/features/teamspace';

import { useBalanceStore } from '@/entities/balance';
import { useUpgradePlanModalStore } from '@/entities/pricing';
import {
  MemberInvite,
  MemberList,
  RemoveMemberModal,
  SpaceColorCircle,
  spaceColorOptions,
  TeamspaceUpgradeAlert,
  useDeleteSpace,
  useGetSpaceById,
  useGetSpaces,
  useLeaveSpace,
  useUpdateSpace,
} from '@/entities/teamspace';
import { useCurrentUser, useSettings } from '@/entities/user/currentUser';

import { PATHS } from '@/shared/constants/routes/paths';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { Form } from '@/shared/ui/form/Form';
import { FormInput } from '@/shared/ui/form/FormInput';
import { FormSelect } from '@/shared/ui/form/FormSelect';
import { Loader } from '@/shared/ui/Loader';

import { useTeamspaceMembersModel } from '../../model/useTeamspaceMembersModel';
import { DeleteConfirmationModal } from './modals/DeleteConfirmationModal';
import { LeaveSpaceConfirmationModal } from './modals/LeaveSpaceConfirmationModal';

const TeamspaceSchema = z.object({
  name: z.string().min(1, 'Field is required'),
  color: z.string().min(1, 'Field is required'),
});

type FormValue = z.infer<typeof TeamspaceSchema>;

type EditTeamspaceFormProps = {
  teamspaceId: string;
  setHasUnsavedChanges?: (value: boolean) => void;
};

export const EditTeamspaceForm = ({ teamspaceId, setHasUnsavedChanges = () => {} }: EditTeamspaceFormProps) => {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const { hasActiveEnterprisePlan, hasActiveSubscription, hasTrialPlan } = useBalanceStore();
  const settings = useSettings();

  const { data: spaces } = useGetSpaces();
  const { data: space, isError: isErrorSpace, isPending: isLoadingSpace } = useGetSpaceById(teamspaceId);
  const { isPending: isUpdatingSpace, mutateAsync: updateSpace } = useUpdateSpace(teamspaceId);
  const { isPending: isLeavingSpace, mutateAsync: leaveSpace } = useLeaveSpace(teamspaceId);
  const { isPending: isDeleteLoading, mutateAsync: deleteSpace } = useDeleteSpace(teamspaceId || '');
  const onCloseSettingsModal = useTeamspaceSettingsModal((state) => state.close);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [removalMember, setRemovalMember] = useState<null | string>(null);

  const {
    canInviteMember,
    isDeletingUser,
    isInvitingMember,
    isOwner,
    members,
    onInviteMember,
    onRemoveInvitedMember,
    onRemoveMember,
  } = useTeamspaceMembersModel(space);


  const userLimit = settings.scope_settings.available_features.spaces.metadata?.user_limit ?? 0;
  const extraSeats = settings.scope_settings.available_features.space_extra_seats.metadata?.quantity ?? 0;
  const totalAllowedSeats = Math.max(userLimit, 1) + extraSeats;

  const defaultSpaceId = spaces?.find((item) => item.is_default)?.id;

  const methods = useForm<FormValue>({
    defaultValues: {
      name: '',
      color: spaceColorOptions[0].value,
    },
    resolver: zodResolver(TeamspaceSchema),
  });

  const { reset } = methods;

  const openUpgradePlanModal = useUpgradePlanModalStore((state) => state.open);

  const onSubmit = async (data: FormValue) => {
    await updateSpace(data);
    setHasUnsavedChanges(false);
  };

  const onDeleteSpaceClick = () => setIsDeleteModalOpen(true);

  const onConfirmLeave = async () => {
    await leaveSpace();
    setIsLeaveModalOpen(false);
    onCloseSettingsModal();
    MixpanelEvents.leaveSpace();
    navigate(`/${PATHS.spaces}/${defaultSpaceId}`);
  };

  const onConfirmRemoveMember = async () => {
    const member = members.find((m) => m.email === removalMember);

    if (!member) return;
    if ('user_id' in member) await onRemoveMember(member.user_id);
    if ('id' in member) await onRemoveInvitedMember(member.id);

    setRemovalMember(null);
  };

  const onDeleteSpace = async () => {
    await deleteSpace();
    navigate('/');
    setIsDeleteModalOpen(false);
    onCloseSettingsModal();
    MixpanelEvents.deleteSpace();
  };

  const onUpgradeClick = () => {
    openUpgradePlanModal({ limitedFeature: 'teamspace', plan: hasActiveEnterprisePlan ? 'custom' : undefined });

    if (hasActiveSubscription && !hasTrialPlan) {
      MixpanelEvents.addMoreMembersUpgradeClick();
    } else {
      MixpanelEvents.addMembersUpgradeClick();
    }
  };

  useEffect(() => {
    setHasUnsavedChanges(methods.formState.isDirty);
  }, [methods.formState.isDirty, setHasUnsavedChanges]);

  // Update form values when data is loaded
  useEffect(() => {
    // don't reset form if it was already fetched with same values
    if (space && space.name !== methods.formState.defaultValues?.name) {
      reset({
        ...(space.name === methods.formState.defaultValues?.name ? {} : { name: space.name }),
        color: space.color || spaceColorOptions[0].value,
      });
    }
  }, [space, reset, methods.formState.defaultValues?.name, methods.formState.defaultValues?.color]);

  return (
    <>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'space-between',
          position: 'relative',
          px: !isLoadingSpace && !isErrorSpace ? {} : { mobile: 16, tablet: 24 },
        }}
        className="tes"
      >
        {isLoadingSpace && <Loader />}

        {isErrorSpace && <SomethingWrongError flex={1} />}

        {!isLoadingSpace && !isErrorSpace && (
          <>
            <Stack sx={{ flex: 1, overflow: 'auto', px: { mobile: 16, tablet: 24 } }}>
              <Form {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                  <Stack gap={20}>
                    <Stack direction="row" gap={8}>
                      <FormInput
                        label="Teamspace name"
                        name="name"
                        control={methods.control}
                        disabled={!isOwner}
                        placeholder="Enter name"
                      />
                      <FormSelect
                        label="Color"
                        name="color"
                        sx={{
                          flexShrink: 0,
                          width: '74px',
                        }}
                        control={methods.control}
                        disabled={!isOwner}
                        options={spaceColorOptions}
                        renderValue={(value) => <SpaceColorCircle colorToken={value as SpeakersPaletteTokens} />}
                        isMenuInModal
                      >
                        {spaceColorOptions.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <SpaceColorCircle colorToken={color.value} />
                          </SelectItem>
                        ))}
                      </FormSelect>
                    </Stack>
                    <Stack gap={12}>
                    <MemberInvite
                      isDisabled={!canInviteMember}
                        isLoading={isInvitingMember}
                        members={members}
                        totalAllowedSeats={totalAllowedSeats}
                        onInvite={onInviteMember}
                      />
                      {isOwner && !canInviteMember && (hasActiveSubscription || hasTrialPlan) && (
                        <TeamspaceUpgradeAlert
                          extraSeatsFeature={settings.scope_settings.available_features.space_extra_seats}
                          spacesFeature={settings.scope_settings.available_features.spaces}
                          onClick={onUpgradeClick}
                        />
                      )}
                      <MemberList
                        email={user?.email}
                        isOwner={isOwner}
                        members={members}
                        onDelete={(email) => setRemovalMember(email)}
                      />
                    </Stack>
                  </Stack>
                </form>
              </Form>
            </Stack>

            <Stack direction="row" spacing={12} sx={{ mt: 3, px: { mobile: 16, tablet: 24 } }} width="100%">
              {isOwner && !space?.is_default && (
                <Button
                  disabled={isUpdatingSpace}
                  size="lg"
                  startIcon={<TrashIcon />}
                  variant="outlined"
                  onClick={onDeleteSpaceClick}
                />
              )}
              {isOwner && (
                <Button
                  isLoading={isUpdatingSpace}
                  size="lg"
                  type="submit"
                  fullWidth
                  onClick={methods.handleSubmit(onSubmit)}
                >
                  Save
                </Button>
              )}

              {!isOwner && (
                <Button
                  size="lg"
                  startIcon={<LogoutIcon />}
                  variant="outlined"
                  fullWidth
                  onClick={() => setIsLeaveModalOpen(true)}
                >
                  Leave Teamspace
                </Button>
              )}
            </Stack>
          </>
        )}
      </Stack>

      <RemoveMemberModal
        isLoading={isDeletingUser}
        isOpen={!!removalMember}
        onCancel={() => setRemovalMember(null)}
        onRemove={onConfirmRemoveMember}
      />
      <DeleteConfirmationModal
        isLoading={isDeleteLoading}
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onDelete={onDeleteSpace}
      />
      <LeaveSpaceConfirmationModal
        isLoading={isLeavingSpace}
        isOpen={isLeaveModalOpen}
        onCancel={() => setIsLeaveModalOpen(false)}
        onLeave={onConfirmLeave}
      />
    </>
  );
};
